// 🔧 passport.js
// ✅ Purpose: Public attendee-facing Engagement Passport page
// ✅ Features:
// - Requires an event slug through useEventContext()
// - Shows active/public Engagement Activities
// - Allows attendees to enter a QR/manual/bonus code
// - Calls redeemEngagementCode Cloud Function when available
// - Displays success/error notifications for 20 seconds
// - Shows Passport progress foundation
// - Uses Mission Control/local emulator-compatible Firebase Functions
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  orderBy, 
} from "firebase/firestore";
import {
  httpsCallable,
} from "firebase/functions";
import { Link } from "react-router-dom";
import { db, functions, auth } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import { logger } from "../../utils/logger";
import { recordEngagement } from "../../services/engagement/engagement_tracker";
import { ENGAGEMENT_EVENTS } from "../../services/engagement/engagement_event_types";
import QrScannerModal from "../../components/scanner/qr_scanner_modal";
import { parseScanPayload } from "../../services/scanner/scan_payload_parser";
import { SCAN_TYPES } from "../../services/scanner/scan_types";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";
import SeoHead from "../../components/seo_head";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";

// ---------------------------------------------------------------------
// Passport Stop icon definitions.
//
// These are the attendee-facing representations of engagement stop types.
// The Firestore activityType remains the internal implementation detail.
// ---------------------------------------------------------------------
const STOP_TYPES = {
  sponsor: {
    icon: "🤝",
    label: "Sponsor Stop",
  },

  vendor: {
    icon: "🛍️",
    label: "Vendor Stop",
  },

  event: {
    icon: "🎪",
    label: "Event Stop",
  },

  bonus: {
    icon: "🎁",
    label: "Bonus Stop",
  },

  hidden: {
    icon: "🗺️",
    label: "Hidden Stop",
  },

  challenge: {
    icon: "🏆",
    label: "Challenge Stop",
  },

  photo: {
    icon: "📸",
    label: "Photo Stop",
  },

  volunteer: {
    icon: "🙋",
    label: "Volunteer Stop",
  },

  education: {
    icon: "🎓",
    label: "Learning Stop",
  },

  default: {
    icon: "📍",
    label: "Passport Stop",
  },
};

const getStopType = (activity = {}) => {
  const safeActivity = activity || {};
  const stopTypeKey = safeActivity.stopType || "default";

  return STOP_TYPES[stopTypeKey] || STOP_TYPES.default;
};

const normalizeCode = (value = "") => {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
};

const Passport = () => {
  const {
    eventId,
    ready,
    eventLoading,
    eventslug,
    eventDoc,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const [activities, setActivities] = useState([]);
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [passportViewRecorded, setPassportViewRecorded] = useState(false);
  // ------------------------------------------------------------
  // Controls the reusable LGV QR Scanner.
  // ------------------------------------------------------------
  const [showScanner, setShowScanner] = useState(false);
  const achievementCount =  summary?.achievementCount || 0;
  const activitiesRef = useMemo(() => {
    if (!eventId) return null;

    return collection(db, "events", eventId, "engagement_activities");
  }, [eventId]);

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    if (!ready || eventLoading || !eventId || passportViewRecorded) return;

    const recordPassportView = async () => {
      try {
        await recordEngagement({
          eventType: ENGAGEMENT_EVENTS.PASSPORT_VIEWED,
          eventId,
          uid: auth.currentUser?.uid || null,
          source: "passport",
          metadata: {
            page: "passport",
            path: window.location.pathname,
          },
        });

        setPassportViewRecorded(true);
      } catch (err) {
        logger.warn("⚠ Failed to record Passport view:", {
          value: err,
        });
      }
    };

    recordPassportView();
  }, [ready, eventLoading, eventId, passportViewRecorded]);

  const loadActivities = async () => {
    if (!activitiesRef) {
      setActivities([]);
      setLoadingActivities(false);
      return;
    }

    try {
      setLoadingActivities(true);

      const snap = await getDocs(
        query(
          activitiesRef,
          where("active", "==", true),
          where("visibleToPublic", "==", true)
        )
      );

      const rows = snap.docs.map((activityDoc) => ({
        id: activityDoc.id,
        ...activityDoc.data(),
      }));

      setActivities(rows);
    } catch (err) {
      // ✅ Log raw callable error details for Mission Control debugging.
      console.error("redeemEngagementCode error code:", err?.code);
      console.error("redeemEngagementCode error message:", err?.message);
      console.error("redeemEngagementCode error details:", err?.details);
      console.error(err);

      logger.error("❌ Failed to redeem Passport code:", {
        value: {
          code: err?.code || null,
          message: err?.message || null,
          details: err?.details || null,
        },
      });

      showStatus(setStatus, {
        type: "error",
        message:
          err?.message ||
          "That code could not be redeemed. Please check the code and try again.",
      });
    } finally {
      setLoadingActivities(false);
    }
  };

  const loadUserSummary = async () => {
    if (!eventId || !auth.currentUser?.uid) {
      setSummary(null);
      return;
    }

    try {
      const summaryRef = doc(
        db,
        "events",
        eventId,
        "engagement_users",
        auth.currentUser.uid
      );

      const summarySnap = await getDoc(summaryRef);

      setSummary(summarySnap.exists() ? summarySnap.data() : null);
    } catch (err) {
      logger.error("❌ Failed to load Passport summary:", {
        value: err,
      });
    }
  };

  useEffect(() => {
    if (!ready || eventLoading) return;

    loadActivities();
    loadUserSummary();
    loadTransactions();
  }, [ready, eventLoading, activitiesRef]);

  const loadTransactions = async () => {
    if (!eventId || !auth.currentUser?.uid) {
      setTransactions([]);
      return;
    }

    try {
      const transactionsRef = collection(
        db,
        "events",
        eventId,
        "engagement_transactions"
      );

      const snap = await getDocs(
        query(
          transactionsRef,
          where("userId", "==", auth.currentUser.uid),
          orderBy("createdAt", "desc")
        )
      );

      setTransactions(
        snap.docs.map((transactionDoc) => ({
          id: transactionDoc.id,
          ...transactionDoc.data(),
        }))
      );
    } catch (err) {
      logger.error("❌ Failed to load Passport transactions:", {
        value: err,
      });
    }
  };

  const handleRedeemCode = async (e) => {
    e.preventDefault();

    const normalizedCode = normalizeCode(code);

    if (!normalizedCode) {
      showStatus(setStatus, {
        type: "error",
        message: "Please enter a Passport code.",
      });

      return;
    }

    if (!auth.currentUser) {
      showStatus(setStatus, {
        type: "error",
        message: "Please sign in before redeeming Passport codes.",
      });

      return;
    }

    try {
      setRedeeming(true);

      const redeemEngagementCode = httpsCallable(
        functions,
        "redeemEngagementCode"
      );

      const result = await redeemEngagementCode({
        eventId,
        code: normalizedCode,
        validationMethod: "manual_code",
      });

      const data = result?.data || {};

      trackEvent({
        action: GA_EVENTS.PASSPORT_CODE_REDEEMED,
        parameters: {
          event_slug: eventslug,
          event_id: eventId,
          validation_method: "manual_code",
          points_awarded: Number(data?.pointsAwarded || 0),
          source_page: window.location.pathname,
        },
      });

      showStatus(setStatus, {
        type: "success",
        message:
          data?.message ||
          `Code accepted. You earned ${data.pointsAwarded || 0} points.`,
      });

      // Clear the entry box after a successful redemption.
      setCode("");

      // Refresh the attendee's Passport information so the UI
      // immediately reflects the newly awarded activity.
      await loadUserSummary();
      await loadTransactions();
    } catch (err) {
      logger.error("❌ Failed to redeem Passport code:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message:
          err?.message ||
          "That code could not be redeemed. Please check the code and try again.",
      });
    } finally {
      setRedeeming(false);
    }
    };

  // ------------------------------------------------------------
  // Handles QR scanner redemption.
  //
  // The scanner simply returns decoded text.
  // Passport parses the payload and reuses the same
  // Cloud Function used by manual code entry.
  // ------------------------------------------------------------
  const handleRedeemScannedCode = async (scannedValue) => {
    const parsedScan = parseScanPayload(scannedValue);

    if (!parsedScan.valid || parsedScan.type !== SCAN_TYPES.PASSPORT) {
      showStatus(setStatus, {
        type: "error",
        message: "This QR code is not a Passport Stop code.",
      });

      return;
    }

    const normalizedCode = normalizeCode(parsedScan.value);

    if (!normalizedCode) {
      return;
    }

    setCode(normalizedCode);

    try {
      setRedeeming(true);

      const redeemEngagementCode = httpsCallable(
        functions,
        "redeemEngagementCode"
      );

      const result = await redeemEngagementCode({
        eventId,
        code: normalizedCode,
        validationMethod: "qr",
      });

      const data = result?.data || {};

      trackEvent({
        action: GA_EVENTS.PASSPORT_QR_SCANNED,
        parameters: {
          event_slug: eventslug,
          event_id: eventId,
          validation_method: "qr",
          points_awarded: Number(data?.pointsAwarded || 0),
          source_page: window.location.pathname,
        },
      });

      trackEvent({
        action: GA_EVENTS.PASSPORT_CODE_REDEEMED,
        parameters: {
          event_slug: eventslug,
          event_id: eventId,
          validation_method: "qr",
          points_awarded: Number(data?.pointsAwarded || 0),
          source_page: window.location.pathname,
        },
      });

      showStatus(setStatus, {
        type: "success",
        message:
          data?.message ||
          `Code accepted. You earned ${data.pointsAwarded || 0} points.`,
      });

      setCode("");

      await loadUserSummary();
      await loadTransactions();

    } catch (err) {
      logger.error("QR redemption failed", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message:
          err?.message ||
          "Unable to redeem QR code.",
      });

    } finally {
      setRedeeming(false);
    }
  };

  const completedActivityIds = useMemo(() => {
    return new Set(
      transactions
        .map((transaction) => transaction.activityId)
        .filter(Boolean)
    );
  }, [transactions]);

  const visibleActivityCount = activities.length;
  const completedActivityCount = activities.filter((activity) =>
    completedActivityIds.has(activity.id)
  ).length;

  const passportComplete =
    visibleActivityCount > 0 &&
    completedActivityCount >= visibleActivityCount;

  if (!ready || eventLoading) {
    return <FullPageSpinner message="Loading Engagement Passport..." />;
  }

  const passportSeo = {
    ...buildEventPageSeo({
      eventDoc,
      pageKey: "passport",
      fallbackTitle: `Engagement Passport | ${eventDoc?.name || "Event"} | Let's Go Vote`,
      fallbackDescription: `Explore engagement activities for ${
        eventDoc?.name || "this event"
      }.`,
    }),
    path: `/${eventslug}/passport`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: eventDoc?.name || "Event", path: `/${eventslug}/event` },
      { name: "Engagement Passport", path: `/${eventslug}/passport` },
    ],
  };

  return (
    <>
    <SeoHead {...passportSeo} />
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1000px" }}>
        <div className="row mb-4">
          <div className="col-12">
            <h1 className="display-5 mb-2">
              🧭 Engagement Passport
            </h1>

            <p className="lead text-muted mb-0">
              Complete activities, visit stops, earn points, and participate in the event experience.
            </p>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        {passportComplete && (
          <div className="alert alert-success shadow-sm">
            <h5 className="fw-bold mb-1">
              🎉 Passport Complete!
            </h5>

            <div>
              You completed all available Passport activities for this event.
            </div>
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Passport Progress</strong>

              <span className="text-muted small">
                {completedActivityCount} of {visibleActivityCount} complete
              </span>
            </div>

            <div className="progress" style={{ height: "12px" }}>
              <div
                className="progress-bar bg-success"
                role="progressbar"
                style={{
                  width:
                    visibleActivityCount > 0
                      ? `${Math.round((completedActivityCount / visibleActivityCount) * 100)}%`
                      : "0%",
                }}
                aria-valuenow={
                  visibleActivityCount > 0
                    ? Math.round((completedActivityCount / visibleActivityCount) * 100)
                    : 0
                }
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
          </div>
        </div>



        <div className="row g-4">
          <div className="col-12 col-lg-5">
            <div className="card shadow-sm mb-4">
              <div className="card-body text-center">

                <button
                  className="btn btn-success btn-lg px-5"
                  onClick={() => {
                    trackEvent({
                      action: GA_EVENTS.PASSPORT_SCANNER_OPENED,
                      parameters: {
                        event_slug: eventslug,
                        event_id: eventId,
                        source: "passport_home",
                        source_page: window.location.pathname,
                      },
                    });

                    setShowScanner(true);
                  }}
                  >
                  📷 Scan Passport Stop
                </button>

                <div className="small text-muted mt-3">
                  Scan the Passport Stop QR code to instantly collect your reward.
                </div>

              </div>
            </div>
            <div className="card shadow-sm">
              <div className="card-header bg-white">
                <strong>Enter Passport Code</strong>
              </div>

              <div className="card-body">
                <form onSubmit={handleRedeemCode}>
                  <label className="form-label fw-bold">
                    Code
                  </label>

                  <input
                    className="form-control form-control-lg text-uppercase"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="SPONSOR25"
                    autoComplete="off"
                  />

                  <div className="form-text">
                    Enter the code from a booth, sponsor, vendor, activity, or hidden stop.
                  </div>

                  <button
                    type="submit"
                    className="btn btn-success w-100 mt-4"
                    disabled={redeeming}
                  >
                    {redeeming ? "Checking Code..." : "Redeem Code"}
                  </button>
                </form>
              </div>
            </div>

            <div className="card shadow-sm mt-4">
              <div className="card-body">
                <h5 className="fw-bold mb-2">
                  Your Passport
                </h5>

                <div className="row g-3 text-center">
                  <div className="col-4">
                    <div className="border rounded p-3">
                      <div className="h4 fw-bold mb-0">
                        {summary?.totalPoints || 0}
                      </div>
                      <div className="small text-muted">
                        Points
                      </div>
                    </div>
                  </div>

                  <div className="col-4">
                    <div className="border rounded p-3">
                      <div className="h4 fw-bold mb-0">
                        {summary?.totalRaffleEntries || 0}
                      </div>
                      <div className="small text-muted">
                        Entries
                      </div>
                    </div>
                  </div>

                  <div className="col-4">
                    <div className="border rounded p-3">
                      <div className="h4 fw-bold mb-0">
                        {summary?.completedActivityCount || 0}
                      </div>
                      <div className="small text-muted">
                        Stops
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <div className="small text-muted">
                  Achievements
                </div>

                <div className="fw-bold">
                  {achievementCount}
                </div>
              </div>

              <Link
                className="btn btn-warning"
                to={`/${eventslug}/passport/achievements`}
              >
                View Badges
              </Link>
            </div>
          </div>

          <div className="card shadow-sm mt-4">
            <div className="card-body">
              <h5 className="fw-bold mb-3">
                Collected Stops
              </h5>

              {transactions.length === 0 ? (
                <p className="text-muted mb-0">
                  Collected Stops will appear here after you redeem Passport codes.
                </p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {transactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="border rounded p-3 bg-white"
                    >
                      <div className="fw-bold">
                        {transaction.activityTitle || "Passport Activity"}
                      </div>

                      <div className="small text-muted">
                        {transaction.points || 0} points ·{" "}
                        {transaction.raffleEntries || 0} entries
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="col-12 col-lg-7">
            <div className="card shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <strong>Passport Stops</strong>

                <span className="badge bg-secondary">
                  {activities.length}
                </span>
              </div>

              <div className="card-body">
                {loadingActivities ? (
                  <div className="text-muted">
                    Loading activities...
                  </div>
                ) : activities.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    No Passport stops are available yet.
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {activities.map((activity) => (
                      <div
                        key={activity.id}
                        className={`border rounded p-3 ${
                          completedActivityIds.has(activity.id)
                            ? "bg-light border-success"
                            : "bg-white"
                        }`}
                        >
                        <div className="d-flex justify-content-between gap-3">
                          <div>
                            <div className="d-flex align-items-center justify-content-between">
                              <div>
                                <h5 className="fw-bold mb-1">
                                  {getStopType(activity).icon}{" "}
                                  {activity.title}
                                </h5>
                                <div className="small text-muted">
                                  {getStopType(activity).icon}{" "}
                                  {getStopType(activity).label}
                                </div>
                                <Link
                                  to={`/${eventslug}/passport/stop/${activity.id}`}
                                  className="btn btn-outline-primary btn-sm mt-3"
                                >
                                  View Stop
                                </Link>                                
                                <Link
                                  to={`/${eventslug}/passport/achievements`}
                                  className="btn btn-outline-warning"
                                >
                                  🏅 Achievements
                                </Link>
                              </div>

                              <div className="text-end">
                                {completedActivityIds.has(activity.id) ? (
                                  <span className="badge bg-success">
                                    ✔ Completed
                                  </span>
                                ) : (
                                  <span className="badge bg-primary">
                                    📍 Stop
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="small text-muted">
                              {activity.activityType || "Passport Stop"}
                            </div>
                          </div>

                          <div className="text-end">
                            <span
                              className={`badge ${
                                completedActivityIds.has(activity.id)
                                  ? "bg-success"
                                  : "bg-secondary"
                              }`}
                            >
                              {completedActivityIds.has(activity.id)
                                ? "Completed"
                                : `${activity.points || 0} pts`}
                            </span>
                          </div>
                        </div>

                        {activity.description && (
                          <p className="text-muted mt-2 mb-0">
                            {activity.description}
                          </p>
                        )}

                        <div className="row mt-3 text-center">
                          <div className="col-6">
                            <div className="border rounded p-2">
                              <div className="fw-bold">
                                +{activity.points || 0}
                              </div>

                              <div className="small text-muted">
                                Points
                              </div>
                            </div>
                          </div>

                          <div className="col-6">
                            <div className="border rounded p-2">
                              <div className="fw-bold">
                                +{activity.raffleEntries || 0}
                              </div>

                              <div className="small text-muted">
                                Prize Entries
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="small text-muted mt-2">
                          Prize entries: {activity.raffleEntries || 0}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="alert alert-light border mt-3 mb-0">
              <strong>Tip:</strong> Some locations may have a public code, while others may provide a bonus code after you talk with a sponsor, vendor, or event team member.
            </div>
          </div>
        </div>
      </div>

      <QrScannerModal
        show={showScanner}
        title="Scan Passport Stop"
        helpText="Scan the Passport Stop QR code."
        onClose={() => setShowScanner(false)}
        onScan={(value) => {
          setShowScanner(false);
          handleRedeemScannedCode(value);
        }}
      />
    </section>
    </>
  );
};

export default Passport;
